import CheckBox from "@/app/components/ui/CheckBoxes/FormCheckbox";
import Input from "@/app/components/ui/Inputs/Input";
import { useMobileNumberValidationAPI } from "@/app/hooks/api_hooks/customer_profile/useMobileNumberValidationAPI";
import useFormValidations from "@/app/hooks/useFormValidations";
import { useRouter } from "@/app/hooks/useRouter";
import { useAppSelector } from "@/app_redux/hooks/root_hook";
import { CheckCircleIcon } from "@heroicons/react/24/outline";
import React, { useEffect } from "react";
import { BiErrorCircle } from "react-icons/bi";
import { useNavigate } from "react-router";
import { ICustomerInfoFormProps } from "../../customer_profile_interface";
import AddressBlock from "./AddressBlock";
import AddressBlockType2 from "./AdressBlockType2";
import RBACFlag from "@/app/components/core/RBACFlag";
import { useFeatureAccess } from "@/app/hooks/useFeatureAccess";

const MobileInputEndIcon: React.FC<{ isValid: boolean }> = ({ isValid }) => {
  return (
    <span>
      {!isValid ? (
        <span>
          <CheckCircleIcon className="w-6 h-6 text-green-500" />
        </span>
      ) : (
        <span className="text-xs text-red-500">
          {" "}
          <BiErrorCircle />{" "}
        </span>
      )}
    </span>
  );
};

const CustomerInfoFormComponent: React.FC<ICustomerInfoFormProps> = ({
  formHook,
  isForUpdate,
  addressLayout,
  addressFieldActions,
}) => {
  const { getRouteKey } = useRouter();
  const navigate = useNavigate();
  const { isCustomerCreationInProgress, isCustomerPetCreationInProgress } = useAppSelector((state) => state.loading);
  const { forEmail, forName, forMobile } = useFormValidations();
  const { isValid, existingUserId, execute: validateMobile } = useMobileNumberValidationAPI();
  const {
    isValid: isAlternateNumberValid,
    existingUserId: existingUserIdAlternative,
    execute: validateAlternateMobile,
  } = useMobileNumberValidationAPI();

  useEffect(() => {
    if (formHook.getValues("mobile")?.length === 10) {
      validateMobile(formHook.getValues("mobile"));
    }
  }, [formHook.watch("mobile")]);

  useEffect(() => {
    if (formHook.getValues("alternate_number")?.length === 10) {
      validateAlternateMobile(formHook.getValues("alternate_number"));
    }
  }, [formHook.watch("alternate_number")]);

  const handleRedirectToCustomerProfile = (customerId: string) => {
    navigate(`${getRouteKey("APPOINTMENTS_TAB", "url")}/${customerId}?new_user=true`);
  };

  // RBAC: CUSTOMER_ADDRESS_BLUECITY_VARIANT — legacy role: clinic_ops_bluecity
  const isBluecity = useFeatureAccess("CUSTOMER_ADDRESS_BLUECITY_VARIANT");
  const disableActionId = useFeatureAccess("USER_MOBILE_DISABLE_ACTION");

  return (
    <div className="pl-2 py-2 sm:py-2 text-xs">
      <p className="mb-4 font-bold font-sans text-gray-800 text-left text-sm">
        {isForUpdate && isForUpdate === true ? (
          <span>
            Update <span>{formHook?.getValues("name")}</span>
          </span>
        ) : (
          <span>Add Customer Information</span>
        )}
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-12 w-full">
        {/* Left Block */}
        <div>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-4 items-start md:items-center w-full">
            <p className="md:col-span-4 font-medium text-black text-xs">
              Customer Name <sup className="text-red-500">*</sup>
            </p>
            <div className="md:col-span-4">
              <Input
                disabled={isCustomerCreationInProgress || isCustomerPetCreationInProgress}
                className="text-xs"
                inputClassName="text-xs"
                placeholder="Enter name"
                register={formHook.register("name", {
                  ...forName.validations,
                  minLength: 1,
                  maxLength: 50,
                  required: true,
                })}
                fieldError={formHook.formState.errors.name}
                errorMessages={[
                  forName.errors,
                  { type: "minLength", message: "Name is too small" },
                  { type: "maxLength", message: "Name is too long" },
                  { type: "required", message: "Name is required" },
                ]}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-4 items-start md:items-center w-full mt-4">
            <p className="md:col-span-4 font-medium text-black text-xs">
              Phone Number <sup className="text-red-500">*</sup>
            </p>

            <div className="md:col-span-8">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {isForUpdate ? (
                  <>
                    <RBACFlag actionId="USER_MOBILE_EDIT_ACTION">
                      <>
                        {" "}
                        <Input
                          disabled={isCustomerCreationInProgress || isCustomerPetCreationInProgress || disableActionId}
                          maxLength={10}
                          className="text-xs"
                          inputClassName="text-xs"
                          endIcon={
                            !isForUpdate &&
                            formHook.watch("mobile")?.length === 10 && <MobileInputEndIcon isValid={isValid} />
                          }
                          placeholder="Enter mobile"
                          register={formHook.register("mobile", {
                            ...forMobile.validations,
                            maxLength: 10,
                            required: true,
                          })}
                          fieldError={formHook.formState.errors.mobile}
                          errorMessages={[
                            forMobile.errors,
                            { type: "required", message: "Phone number is required" },
                            { type: "maxLength", message: "Invalid phone number" },
                          ]}
                        />
                        <div className="flex flex-col justify-start items-start sm:items-start pt-1 sm:pt-0">
                          <CheckBox
                            disabled={isCustomerCreationInProgress || isCustomerPetCreationInProgress}
                            labelTitle="Set as Whatsapp"
                            errorMessages={[]}
                            fieldError={formHook.formState.errors.is_whatsapp_1}
                            register={formHook.register("is_whatsapp_1")}
                          />
                        </div>
                      </>
                    </RBACFlag>
                  </>
                ) : (
                  <>
                    <Input
                      disabled={isCustomerCreationInProgress || isCustomerPetCreationInProgress}
                      maxLength={10}
                      className="text-xs"
                      inputClassName="text-xs"
                      endIcon={
                        !isForUpdate &&
                        formHook.watch("mobile")?.length === 10 && <MobileInputEndIcon isValid={isValid} />
                      }
                      placeholder="Enter mobile"
                      register={formHook.register("mobile", {
                        ...forMobile.validations,
                        maxLength: 10,
                        required: true,
                      })}
                      fieldError={formHook.formState.errors.mobile}
                      errorMessages={[
                        forMobile.errors,
                        { type: "required", message: "Phone number is required" },
                        { type: "maxLength", message: "Invalid phone number" },
                      ]}
                    />
                    <div className="flex flex-col justify-start items-start sm:items-start pt-1 sm:pt-0">
                      <CheckBox
                        disabled={isCustomerCreationInProgress || isCustomerPetCreationInProgress}
                        labelTitle="Set as Whatsapp"
                        errorMessages={[]}
                        fieldError={formHook.formState.errors.is_whatsapp_1}
                        register={formHook.register("is_whatsapp_1")}
                      />
                    </div>
                  </>
                )}
              </div>
              {!isForUpdate && existingUserId && isValid && formHook.watch("mobile") && (
                <span className="block mt-2 text-gray-500 text-xs">
                  user already exists,{" "}
                  <span
                    onClick={() => handleRedirectToCustomerProfile(existingUserId)}
                    className="inline-block text-blue-500 underline cursor-pointer"
                  >
                    View Profile
                  </span>{" "}
                </span>
              )}
            </div>
          </div>

          {/* Customer Alternate Mobile */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-4 items-start md:items-center w-full mt-4">
            <p className="md:col-span-4 font-medium text-black text-xs">Alternate Mobile</p>
            <div className="md:col-span-8">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <Input
                  disabled={isCustomerCreationInProgress || isCustomerPetCreationInProgress}
                  maxLength={10}
                  endIcon={
                    !isForUpdate &&
                    formHook.watch("alternate_number")?.length === 10 && (
                      <MobileInputEndIcon isValid={isAlternateNumberValid} />
                    )
                  }
                  inputClassName="text-xs"
                  placeholder="Enter alternate mobile"
                  className="text-xs"
                  register={formHook.register("alternate_number", {
                    ...forMobile.validations,
                    maxLength: 10,
                  })}
                  fieldError={formHook.formState.errors.alternate_number}
                  errorMessages={[forMobile.errors, { type: "maxLength", message: "Invalid phone number" }]}
                />

                <div className="flex flex-col justify-start items-start sm:items-start pt-1 sm:pt-0">
                  <CheckBox
                    disabled={isCustomerCreationInProgress || isCustomerPetCreationInProgress}
                    labelTitle="Set as Whatsapp"
                    errorMessages={[]}
                    register={formHook.register("is_whatsapp_2")}
                    fieldError={formHook.formState.errors.is_whatsapp_2}
                  />
                </div>
              </div>
            </div>
          </div>
          {/* <div className="grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-4 items-start md:items-center w-full mt-4">
            <p className="md:col-span-4 font-medium text-black text-xs">GST Number</p>
            <div className="md:col-span-8">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <Input
                  disabled={isCustomerCreationInProgress || isCustomerPetCreationInProgress}
                  inputClassName="text-xs"
                  placeholder="Enter GST Number"
                  className="text-xs"
                  register={formHook.register("gst_no")}
                  fieldError={formHook.formState.errors.gst_no}
                  errorMessages={[]}
                />
              </div>
            </div>
          </div> */}
          {!isForUpdate &&
            existingUserIdAlternative &&
            isAlternateNumberValid &&
            formHook.watch("alternate_number") && (
              <span className="block mt-2 text-gray-500 text-xs">
                user already exists,{" "}
                <span
                  onClick={() => handleRedirectToCustomerProfile(existingUserIdAlternative)}
                  className="inline-block text-blue-500 underline cursor-pointer"
                >
                  View Profile
                </span>{" "}
              </span>
            )}
          {formHook.watch("alternate_number") === formHook.watch("mobile") &&
            formHook.watch("alternate_number")?.length === 10 &&
            formHook.watch("mobile")?.length === 10 && (
              <p className="mt-2 text-red-500 text-xs">Primary and Alternative number can not be same</p>
            )}
        </div>

        {/* Right Block */}
        <div>
          {/* Customer Email */}
          <div className="grid grid-cols-1 md:grid-cols-[140px,1fr] gap-2 md:gap-3 items-start md:items-center w-full">
            <p className="font-medium text-black text-xs md:whitespace-nowrap">Email ID</p>
            <div className="max-w-[200px]">  {/* ← constrain width */}
              <Input
                disabled={isCustomerCreationInProgress || isCustomerPetCreationInProgress}
                className="text-xs"
                inputClassName="text-xs"
                placeholder="Enter Email ID"
                register={formHook.register("email", {
                  ...forEmail.validations,
                })}
                fieldError={formHook.formState.errors.email}
                errorMessages={[forEmail.errors]}
              />
            </div>
          </div>

          {/* GST Number — moved from left block to here */}
          <div className="grid grid-cols-1 md:grid-cols-[140px,1fr] gap-2 md:gap-3 items-start md:items-center w-full mt-4">
            <p className="font-medium text-black text-xs md:whitespace-nowrap">GST Number</p>
            <div className="max-w-[200px]">  {/* ← matches email width */}
              <Input
                disabled={isCustomerCreationInProgress || isCustomerPetCreationInProgress}
                inputClassName="text-xs"
                placeholder="Enter GST Number"
                className="text-xs"
                register={formHook.register("gst_no")}
                fieldError={formHook.formState.errors.gst_no}
                errorMessages={[]}
              />
            </div>
          </div>

          {/* Customer Address */}
          <div className="mt-4 w-full">
            {addressLayout === "SINGLE" ? (
              <div className="grid grid-cols-1 md:grid-cols-[140px,1fr] gap-2 md:gap-3 items-start w-full">
                <p className="font-medium text-black text-xs md:whitespace-nowrap pt-2">
                  Address {!isBluecity && <sup className="text-red-500">*</sup>}
                </p>
                <div className="w-full">
                  <AddressBlock
                    addressFieldActions={addressFieldActions}
                    disabled={isCustomerCreationInProgress || isCustomerPetCreationInProgress}
                    isFormUpdate={isForUpdate && isForUpdate === true ? true : false}
                    formHook={formHook}
                  />
                </div>
              </div>
            ) : (
              <> </>
            )}
          </div>

          {/* Customer adress block Mobile */}
        </div>
      </div>
      {isForUpdate && isForUpdate === true && addressLayout === "MULTI" && (
        <div className="mt-2 py-2">
          <AddressBlockType2
            addressFieldActions={addressFieldActions}
            disabled={isCustomerCreationInProgress || isCustomerPetCreationInProgress}
            isFormUpdate={isForUpdate && isForUpdate === true ? true : false}
            formHook={formHook}
          />
        </div>
      )}
    </div>
  );
};

export default CustomerInfoFormComponent;
